import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Admin-only: a small "{ } View JSON" trigger that opens a movable, NON-modal floating panel showing the
// formatted JSON behind a solution card. Non-modal (no backdrop) and draggable by its header on purpose, so an
// admin can park it beside the card and read both at once — comparing the card to the exact field names/values
// that drive it (for example material_layout_dimension — the sheet — versus the part's own layout_dimension).
//
// The panel is rendered through a portal into document.body, NOT inside the card. That is deliberate: the card
// wrapper has a :hover rule with transform: translateY(-2px), and a transform on an ancestor re-bases a
// position: fixed descendant onto that ancestor. Were the panel a child of the card, hovering it would shift
// it, drop the hover, shift it back, re-hover — an endless flicker loop (and it also broke dragging). Portaling
// out of the card removes the panel from the card's hover subtree entirely, so neither happens.
//
// Gated by the caller on window.isAdminShowDebug.
const PANEL_WIDTH = 660;

// JSONView-style colours for the formatted JSON, applied with a tiny self-contained tokenizer (no dependency).
const JSON_COLORS = { key: '#a31515', string: '#0b7500', number: '#1a01cc', boolean: '#0000ff', null: '#795548' };

// Turn a pretty-printed JSON string into colour-highlighted HTML. The input is HTML-escaped first, so the
// resulting markup is safe to inject (and the data is our own server's solution payload, admin-only).
const highlightJson = (json) => {
  const escaped = json
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  // Match, in order: quoted strings that are object keys (followed by a colon), other quoted strings,
  // booleans, null, then numbers.
  const token = /("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"\s*:)|("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*")|(\btrue\b|\bfalse\b)|(\bnull\b)|(-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g;
  return escaped.replace(token, (match, isKey, isString, isBoolean, isNull) => {
    let color = JSON_COLORS.number;
    if (isKey) {
      color = JSON_COLORS.key;
    } else if (isString) {
      color = JSON_COLORS.string;
    } else if (isBoolean) {
      color = JSON_COLORS.boolean;
    } else if (isNull) {
      color = JSON_COLORS.null;
    }
    return `<span style="color:${color}">${match}</span>`;
  });
};

const SolutionJsonViewer = ({ data }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [position, setPosition] = useState({ x: 80, y: 80 });
  // Drag offset between the cursor and the panel's top-left, held in a ref so a drag does not re-render per move.
  const dragOffset = useRef(null);

  const formatted = (() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch (error) {
      return `// Could not serialize solution JSON: ${error.message}`;
    }
  })();

  // Close the panel on Escape while it is open.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // While dragging, follow the cursor on document-level listeners (so the drag continues even if the cursor
  // leaves the small header), and clean them up on release or when the panel closes. When not dragging
  // (dragOffset is null) the move handler returns immediately, so plain hovering never triggers a re-render.
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onMouseMove = (event) => {
      if (!dragOffset.current) {
        return;
      }
      setPosition({ x: event.clientX - dragOffset.current.x, y: event.clientY - dragOffset.current.y });
    };
    const onMouseUp = () => {
      dragOffset.current = null;
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [open]);

  const stop = (event) => {
    event.stopPropagation();
  };

  const openPanel = (event) => {
    stop(event);
    // Open beside the card by default: pinned to the right edge of the viewport, near the top. The admin can
    // then drag it anywhere. Computed on open so it respects the current viewport width.
    const x = Math.max(20, window.innerWidth - PANEL_WIDTH - 24);
    setPosition({ x, y: 80 });
    setOpen(true);
  };

  const startDrag = (event) => {
    stop(event);
    dragOffset.current = { x: event.clientX - position.x, y: event.clientY - position.y };
  };

  const copy = (event) => {
    stop(event);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(formatted).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }).catch(() => { /* clipboard blocked — the admin can still select the text manually */ });
    }
  };

  const panel = (
    <div
      onClick={stop}
      style={{
        position: 'fixed', top: `${position.y}px`, left: `${position.x}px`, zIndex: 10000,
        width: `${PANEL_WIDTH}px`, maxWidth: 'calc(100vw - 40px)', height: '70vh',
        minWidth: '280px', minHeight: '160px', resize: 'both', overflow: 'hidden',
        background: '#fff', border: '1px solid #ccc', borderRadius: '8px',
        display: 'flex', flexDirection: 'column', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)'
      }}
    >
      <div
        onMouseDown={startDrag}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderBottom: '1px solid #eee', cursor: 'move',
          background: '#fafafa', userSelect: 'none', borderTopLeftRadius: '8px', borderTopRightRadius: '8px'
        }}
      >
        <strong style={{ fontSize: '13px' }}>⠿ Solution JSON</strong>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button type="button" onMouseDown={stop} onClick={copy} style={{ padding: '3px 9px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}>
            {copied ? 'Copied' : 'Copy'}
          </button>
          <button type="button" onMouseDown={stop} onClick={(event) => { stop(event); setOpen(false); }} style={{ padding: '3px 9px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}>
            Close
          </button>
        </div>
      </div>
      <pre
        style={{ margin: 0, padding: '12px', overflow: 'auto', flex: 1, fontSize: '10px', lineHeight: 1.45, fontFamily: 'monospace', whiteSpace: 'pre' }}
        dangerouslySetInnerHTML={{ __html: highlightJson(formatted) }}
      />
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={openPanel}
        title="View the JSON that represents this solution (admin only)"
        style={{
          display: 'inline-block', marginTop: '6px',
          padding: '2px 10px', fontSize: '12px', fontFamily: 'monospace', fontWeight: 600,
          color: '#555', background: '#f4f4f4', border: '1px solid #ddd', borderRadius: '20px', cursor: 'pointer'
        }}
      >
        { '{ } View JSON' }
      </button>

      {open && createPortal(panel, document.body)}
    </>
  );
};

export default SolutionJsonViewer;
